'use client';

import { useEffect, useRef } from 'react';

/**
 * Tom de telefone via Web Audio API (sem arquivo de audio). Dois modos:
 *  - 'incoming'  (padrao): 2 beeps curtos + pausa a cada 1.5s — chamada RECEBIDA.
 *  - 'ringback': tom longo de ~1s a cada 4s — o "puuu...." de chamada de SAIDA.
 *    Necessario porque o browser NAO gera ringback sozinho ao receber 180 Ringing
 *    (softphone/telefone IP geram nativamente; webphone precisa deste tom). E o
 *    ringback server-side (Dial `r`) nao chega no browser: a midia WebRTC (DTLS)
 *    so sobe no atendimento, entao nao ha caminho de audio na fase "chamando".
 *
 * Issue #493 — dois defeitos corrigidos aqui:
 *
 * 1. O Chrome cria o AudioContext SUSPENSO quando a aba ainda nao recebeu gesto
 *    do usuario desde que carregou. Numa aba recarregada (F5) em que o agente
 *    nao clicou em nada, a ligacao chegava e NAO saia som — sem erro, sem
 *    sintoma visivel. Agora o contexto e retomado com `resume()` e, se o
 *    navegador recusar (exige gesto), o toque destrava no primeiro clique/tecla.
 *
 * 2. A cadencia usava `setInterval`, e o navegador estrangula timer de aba em
 *    segundo plano para no maximo 1 disparo por minuto — o agente ouvia ~1 bipe
 *    por minuto. Os beeps agora sao AGENDADOS no relogio do proprio
 *    AudioContext, que nao sofre esse estrangulamento; o timer apenas reabastece
 *    a fila, e uma janela maior que 60s garante que um reabastecimento atrasado
 *    nunca deixe buraco audivel.
 *
 * @param playing quando true, toca. Quando false, para e libera o AudioContext.
 * @param mode qual cadencia tocar (default 'incoming', preserva chamadas existentes).
 */

const FREQ_HZ = 480; // tom de telefone classico
const VOLUME = 0.05;

/** Cadencias, em segundos: periodo do ciclo e os beeps dentro dele. */
const CADENCIA = {
  incoming: {
    periodo: 1.5,
    beeps: [
      { em: 0, dur: 0.25 },
      { em: 0.35, dur: 0.25 },
    ],
  },
  ringback: {
    periodo: 4,
    // ~1s tocando, ~3s de silencio — cadencia de chamada de saida no Brasil.
    beeps: [{ em: 0, dur: 1.0 }],
  },
} as const;

/**
 * Quanto de cadencia fica agendado a frente. Tem que passar de 60s: esse e o
 * teto do estrangulamento de timer em aba de segundo plano (1 disparo/min), e
 * com mais de 60s na fila o reabastecimento atrasado ainda chega antes do fim.
 */
const JANELA_S = 90;
const REABASTECER_MS = 30_000;

/** Eventos que contam como "gesto do usuario" pro navegador destravar o audio. */
const GESTOS = ['pointerdown', 'keydown', 'touchstart'] as const;

export function useRingtone(playing: boolean, mode: 'incoming' | 'ringback' = 'incoming') {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Ate que instante do relogio do contexto a cadencia ja foi agendada. */
  const agendadoAteRef = useRef(0);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
      return;
    }

    // Cria/reuse AudioContext
    if (!ctxRef.current) {
      try {
        const AudioCtx =
          (window as unknown as {
            AudioContext: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        ctxRef.current = new AudioCtx();
        agendadoAteRef.current = 0;
      } catch {
        return;
      }
    }

    const beep = (inicio: number, dur: number) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = FREQ_HZ;
      osc.type = 'sine';
      gain.gain.value = VOLUME;
      osc.connect(gain).connect(ctx.destination);
      osc.start(inicio);
      osc.stop(inicio + dur);
    };

    /**
     * Enfileira cadencia ate `JANELA_S` a frente. Enquanto o contexto esta
     * suspenso o `currentTime` fica congelado, entao agendar antes do resume e
     * seguro: os instantes sao relativos ao relogio do proprio contexto e o
     * `resume()` retoma de onde parou, sem despejar tudo de uma vez.
     */
    const agendar = () => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const { periodo, beeps } = CADENCIA[mode];
      const limite = ctx.currentTime + JANELA_S;
      let t = Math.max(ctx.currentTime, agendadoAteRef.current);
      while (t < limite) {
        for (const b of beeps) beep(t + b.em, b.dur);
        t += periodo;
      }
      agendadoAteRef.current = t;
    };

    const aoGesto = () => {
      limparGestos();
      tentarRetomar();
    };
    const limparGestos = () => {
      for (const ev of GESTOS) window.removeEventListener(ev, aoGesto, true);
    };

    /**
     * Sem nenhum gesto na aba, o navegador REJEITA o resume(). Nesse caso o
     * unico caminho e esperar o primeiro clique/tecla — inclusive o clique no
     * proprio botao "Atender", que passa a destravar o audio da sessao.
     */
    const tentarRetomar = () => {
      const ctx = ctxRef.current;
      if (!ctx || ctx.state !== 'suspended') return;
      ctx.resume().catch(() => {
        for (const ev of GESTOS) window.addEventListener(ev, aoGesto, { capture: true });
      });
    };

    agendar();
    tentarRetomar();
    timerRef.current = setInterval(agendar, REABASTECER_MS);

    return () => {
      limparGestos();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Revisão 16/07: fechar o AudioContext TAMBÉM aqui. O ramo `if (!playing)` só
      // fecha na transição playing→false; no UNMOUNT com playing=true (o comum: o modal
      // de incoming desmonta no instante que a chamada é atendida/rejeitada) o cleanup
      // rodava sem fechar → vazava 1 contexto por chamada. Depois de ~6, o Chrome lança
      // em `new AudioContext()`, engolido pelo try/catch, e o ramal fica MUDO de ringtone
      // o resto da sessão. Fechar tambem cancela tudo que estava agendado a frente.
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
    };
  }, [playing, mode]);
}
